import { useTranslation } from 'helpers/hooks/useTranslation'
import React, { FC, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useViewportEffect } from 'react-viewport-utils'
import styled, { css, keyframes } from 'styled-components'
import { complementaryColor } from 'uiComponents/constants/colors'
import {
  fontFamilyPrimary,
  fsS,
  fsXS,
  fsXXXXL,
  fwBold,
  fwMediumBold,
} from 'uiComponents/constants/typography'

const textOpacityAnimation = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const FadingSpan = styled.span`
  animation: ${textOpacityAnimation} 1s cubic-bezier(0.65, 0.28, 0.4, 0.82);
  animation-play-state: paused;
`

const FadeText: FC<
  React.PropsWithChildren<{ dataTestId?: string; className?: string }>
> = ({ dataTestId, children, className }) => {
  const { ref } = useTextFadeIn<HTMLSpanElement>()

  return (
    <FadingSpan
      data-testid={dataTestId ? dataTestId : 'FadeText'}
      className={className}
      ref={ref}
    >
      {children}
    </FadingSpan>
  )
}

const Img = styled.img`
  object-fit: cover;
  object-position: center top;

  position: absolute;
  height: 200%;
  width: 100%;
`
const FallbackImg = styled.img`
  flex: 0;
  width: 100%;
  height: 100%;
  min-height: 100%;
  background-position: top;
  object-fit: cover;
  object-position: top;
`

export const ImgBackdrop = styled.div`
  flex: 0;
  width: 100%;
  height: 100%;
  min-height: 100%;
  z-index: 1;
  background-color: rgba(0, 0, 0, 0.4);
`

const Wrapper = styled.div`
  position: relative;
  height: 50vw;
  min-height: 375px;
  max-height: 640px;
  width: 100%;
  overflow-x: hidden;
  overflow-y: hidden;
  background: #e1e1e1;
`

const parallaxLayer = css`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`

const Foreground = styled.div`
  ${parallaxLayer};
  z-index: 1;
`
const Background = styled.div`
  ${parallaxLayer};
  height: 100%;
  width: 100%;
  flex-shrink: 0;
`

const Title = styled(FadeText)`
  ${fontFamilyPrimary};
  ${fwBold};
  color: ${complementaryColor};
  font-size: ${fsXXXXL}px;
  text-align: center;
  margin: 24px 0 50px;
  padding: 0 8px;
`
export const StoryIntroduction = styled.p`
  ${fontFamilyPrimary};
  ${fwBold};
  color: ${complementaryColor};
  text-transform: uppercase;
  font-size: ${fsXS}px;
  text-align: center;
  letter-spacing: 1.2px;
  margin-top: 50px;
`
export const StoryDiscover = styled.p`
  ${fontFamilyPrimary};
  ${fwMediumBold};
  color: ${complementaryColor};
  font-size: ${fsS}px;
  text-align: center;
  line-height: 1.5;
  letter-spacing: 0.4px;
  padding-bottom: 5px;
  border-bottom: 1px solid ${complementaryColor};
`

const useImageParallax = <E extends HTMLElement>(
  disabled?: boolean,
  imageRef = useRef<E>(null),
  wrapperRef = useRef<E>(null),
) => {
  const [isCloseToViewPort, setCloseToViewPort] = useState(false)

  const recalculateLayoutBeforeUpdate = () => {
    if (imageRef.current && wrapperRef.current) {
      return {
        imageRect: imageRef.current.getBoundingClientRect(),
        wrapperRect: wrapperRef.current.getBoundingClientRect(),
      }
    }
    return null
  }
  const updateConf = {
    deferUpdateUntilIdle: false,
    priority: isCloseToViewPort ? 'highest' : 'low',
    recalculateLayoutBeforeUpdate,
  } as const

  useViewportEffect(({ dimensions: { height: viewportHeight } }, sizeData) => {
    if (sizeData && imageRef.current && !disabled) {
      const {
        wrapperRect: { top, bottom, height: wrapperHeight },
        imageRect: { height: imageHeight },
      } = sizeData

      const topProgress = (viewportHeight - top) / viewportHeight
      const bottomProgress = (viewportHeight - bottom) / viewportHeight

      const getsCloseToViewport = -0.5 < topProgress && bottomProgress < 1.5

      if (getsCloseToViewport !== isCloseToViewPort) {
        setCloseToViewPort(getsCloseToViewport)
      }
      const parallaxSpace = Math.round((imageHeight - wrapperHeight) / 2)

      const yShift = Math.round(topProgress * parallaxSpace)

      if (getsCloseToViewport) {
        imageRef.current.style.transform = `translate3D(0,${yShift}px,0)`
      }
    }
  }, updateConf)

  return { imageRef, wrapperRef }
}

const useTextFadeIn = <E extends HTMLElement>(ref = useRef<E>(null)) => {
  const [isCloseToViewPort, setCloseToViewPort] = useState(false)

  const recalculateLayoutBeforeUpdate = () => {
    if (ref.current) {
      return ref.current.getBoundingClientRect()
    }
    return null
  }
  const updateConf = {
    deferUpdateUntilIdle: false,
    priority: isCloseToViewPort ? 'high' : 'low',
    recalculateLayoutBeforeUpdate,
  } as const

  useViewportEffect(({ dimensions: { height: viewportHeight } }, rect) => {
    if (rect && ref.current) {
      const { top, bottom } = rect
      const topProgress = (viewportHeight - top) / viewportHeight
      const bottomProgress = (viewportHeight - bottom) / viewportHeight

      const getsCloseToViewport = -0.5 < topProgress && bottomProgress < 1.5
      if (getsCloseToViewport !== isCloseToViewPort) {
        setCloseToViewPort(getsCloseToViewport)
      }

      if (getsCloseToViewport) {
        ref.current.style.animationDelay = `-${topProgress}s`
      }
    }
  }, updateConf)
  return { ref }
}

export interface StoryHeaderImageProps {
  storyName: string
  storyImageSrc: string
  storyHref: string
  disableParallax?: boolean
}
const StoryHeaderImage: FC<React.PropsWithChildren<StoryHeaderImageProps>> = ({
  storyName,
  storyHref,
  storyImageSrc,
  disableParallax,
}) => {
  const { t } = useTranslation()
  const { imageRef, wrapperRef } =
    useImageParallax<HTMLImageElement>(disableParallax)

  return (
    <Wrapper data-testid="StoryHeaderImage">
      <Link data-testid="link" to={storyHref}>
        <Background data-testid="BackgroundImage" ref={wrapperRef}>
          {disableParallax ? (
            <FallbackImg src={storyImageSrc} />
          ) : (
            <Img ref={imageRef} src={storyImageSrc} />
          )}
          <ImgBackdrop />
        </Background>
        <Foreground data-testid="Foreground">
          <StoryIntroduction data-testid="StoryIntroduction">
            <FadeText>{t('Passende Inspiration für dich')}</FadeText>
          </StoryIntroduction>
          <Title dataTestId="StoryName">{storyName}</Title>
          <FadeText>
            <StoryDiscover data-testid="StoryDiscover">
              {t('Mehr entdecken')}
            </StoryDiscover>
          </FadeText>
        </Foreground>
      </Link>
    </Wrapper>
  )
}

export default StoryHeaderImage
